import type { ReviewRun } from './use-review-statuses';

export const OPTIMISTIC_REVIEW_TIMEOUT_MS = 60_000;

/**
 * Optimistic runs are local placeholders created before backend rows exist.
 */
export const isPendingOptimisticRun = (run: ReviewRun): boolean => run.isPendingOptimistic === true;

/**
 * Pending optimistic runs represent client-side placeholders while backend jobs are queued.
 * They time out after 60s to avoid endless spinner UI when the run never materializes.
 */
export const isTimedOutPendingRun = (run: ReviewRun, nowMs: number = Date.now()): boolean => {
  if (!isPendingOptimisticRun(run) || !run.optimisticRequestedAt) {
    return false;
  }

  const requestedAtMs = new Date(run.optimisticRequestedAt).getTime();
  if (!Number.isFinite(requestedAtMs)) {
    return false;
  }

  return nowMs - requestedAtMs >= OPTIMISTIC_REVIEW_TIMEOUT_MS;
};

const matchesOptimisticIdentity = (optimisticRun: ReviewRun, realRun: ReviewRun): boolean => {
  if (!optimisticRun.traceId || realRun.traceId !== optimisticRun.traceId) {
    return false;
  }

  const isCompatibleRunKind =
    optimisticRun.runKind === realRun.runKind ||
    // Trigger requests can create general/rule optimistic placeholders while
    // the backend materializes a single batch run for the same trace.
    (realRun.runKind === 'batch' &&
      (optimisticRun.runKind === 'general' || optimisticRun.runKind === 'rule'));

  if (!isCompatibleRunKind) {
    return false;
  }

  // For rule-to-rule reconciliation we still require rule identity.
  if (optimisticRun.runKind === 'rule' && realRun.runKind === 'rule') {
    return !!optimisticRun.ruleId && realRun.ruleId === optimisticRun.ruleId;
  }

  return true;
};

const isResolvedByRealRun = (optimisticRun: ReviewRun, realRuns: ReviewRun[]): boolean => {
  if (!optimisticRun.traceId) {
    return false;
  }

  return realRuns.some((realRun) => matchesOptimisticIdentity(optimisticRun, realRun));
};

/**
 * Reconciles persisted runs with optimistic placeholders.
 * - Always keep persisted runs from the backend.
 * - Keep optimistic runs until a matching persisted run materializes.
 */
export function reconcileReviewRuns(
  fetchedRuns: ReviewRun[],
  cachedRuns: ReviewRun[],
  nowMs: number = Date.now(),
): ReviewRun[] {
  const mergedByReviewRunId = new Map<string, ReviewRun>();
  const persistedRuns: ReviewRun[] = [];

  const cachedByReviewRunId = new Map(cachedRuns.map((run) => [run.reviewRunId, run]));

  for (const run of fetchedRuns) {
    // Realtime merges can pass optimistic rows via fetchedRuns; only seed persisted rows here.
    // Optimistic placeholders are reconciled from cachedRuns below so resolved ones can be dropped.
    if (isPendingOptimisticRun(run)) {
      continue;
    }

    // Preserve realtime-sourced progress fields when the poll response doesn't include them.
    // The REST API doesn't return progress data, but realtime events do.
    const cachedRun = cachedByReviewRunId.get(run.reviewRunId);
    if (
      run.status === 'running' &&
      cachedRun?.progressPercentage != null &&
      run.progressPercentage == null
    ) {
      run.progressPercentage = cachedRun.progressPercentage;
      run.progressMessage = cachedRun.progressMessage;
      run.filesProcessed = cachedRun.filesProcessed;
      run.totalFiles = cachedRun.totalFiles;
    }

    persistedRuns.push(run);
    mergedByReviewRunId.set(run.reviewRunId, run);
  }

  const optimisticRuns = cachedRuns.filter(isPendingOptimisticRun);

  for (const optimisticRun of optimisticRuns) {
    if (isResolvedByRealRun(optimisticRun, persistedRuns)) {
      continue;
    }

    const optimisticRequestedAt = optimisticRun.optimisticRequestedAt ?? optimisticRun.createdAt;

    // Keep unresolved optimistic placeholders. UI components decide how to render timed-out ones.
    const normalizedOptimisticRun: ReviewRun = {
      ...optimisticRun,
      updatedAt: optimisticRun.updatedAt ?? optimisticRun.createdAt,
      isPendingOptimistic: true,
      optimisticRequestedAt,
    };

    // Store timeout evaluation deterministically via updatedAt to keep display ordering stable.
    if (isTimedOutPendingRun(normalizedOptimisticRun, nowMs)) {
      normalizedOptimisticRun.updatedAt = optimisticRequestedAt;
    }

    mergedByReviewRunId.set(normalizedOptimisticRun.reviewRunId, normalizedOptimisticRun);
  }

  return Array.from(mergedByReviewRunId.values());
}

/**
 * Counts optimistic placeholders that exceeded the timeout window.
 */
export const countTimedOutPendingRuns = (runs: ReviewRun[], nowMs: number = Date.now()): number =>
  runs.filter((run) => isTimedOutPendingRun(run, nowMs)).length;
